import { useState, useCallback, useEffect, useRef } from 'react';
import type { FormInstance } from 'antd';
import { message } from 'antd';
import { EstimateLineItem, Part, Customer, Vehicle, Estimate } from '../../types';
import api from '../../services/api';
import { logEvent, newEventId } from '../../services/eventLogger';
import type { CustomerMatch } from '../../utils/customerMatcher';
import type { VehicleMatch } from '../../utils/vehicleMatcher';
import dayjs from 'dayjs';

interface UseEstimateAIProps {
  form: FormInstance;
  formContext: any;
  estimate: Estimate | null;
  lineItems: EstimateLineItem[];
  setLineItems: React.Dispatch<React.SetStateAction<EstimateLineItem[]>>;
  selectedCustomer: Customer | null;
  setSelectedCustomer: React.Dispatch<React.SetStateAction<Customer | null>>;
  selectedVehicle: Vehicle | null;
  setSelectedVehicle: React.Dispatch<React.SetStateAction<Vehicle | null>>;
  setHasUnsavedChanges: (v: boolean) => void;
  defaultLaborRate: number;
}

export function useEstimateAI({
  form, formContext, estimate, lineItems, setLineItems,
  selectedCustomer, setSelectedCustomer, selectedVehicle, setSelectedVehicle,
  setHasUnsavedChanges, defaultLaborRate,
}: UseEstimateAIProps) {
  const { aiAnalysis } = formContext;
  const [aiExtractedData, setAiExtractedData] = useState<any>(null);
  const [customerCandidates, setCustomerCandidates] = useState<CustomerMatch[]>([]);
  const [vehicleCandidates, setVehicleCandidates] = useState<VehicleMatch[]>([]);

  // ── Customer match callbacks ──
  const handleCustomerAutoSelected = useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    form.setFieldValue('customer_id', customer.id);
    setHasUnsavedChanges(true);
    formContext.markFieldDirty('customer.first_name');
    formContext.markFieldDirty('customer.last_name');
    formContext.markFieldDirty('customer.phone');
    formContext.clearAICustomerFields();
    formContext.onCustomerSaved(customer);
    setCustomerCandidates([]);
    for (const key of Object.keys(formContext.fieldConfidences)) {
      if (key.startsWith('customer.')) formContext.markFieldReviewed(key);
    }
    message.success(`Auto-selected: ${customer.first_name} ${customer.last_name}`);
  }, [formContext, form, setSelectedCustomer, setHasUnsavedChanges]);

  const handleCustomerCandidatesFound = useCallback((candidates: CustomerMatch[]) => {
    setCustomerCandidates(candidates);
  }, []);

  const handleAcceptCustomerCandidate = useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    form.setFieldValue('customer_id', customer.id);
    setHasUnsavedChanges(true);
    formContext.markFieldDirty('customer.first_name');
    formContext.markFieldDirty('customer.last_name');
    formContext.markFieldDirty('customer.phone');
    formContext.clearAICustomerFields();
    formContext.onCustomerSaved(customer);
    setCustomerCandidates([]);
    for (const key of Object.keys(formContext.fieldConfidences)) {
      if (key.startsWith('customer.')) formContext.markFieldReviewed(key);
    }
    message.success(`Selected: ${customer.first_name} ${customer.last_name}`);
  }, [formContext, form, setSelectedCustomer, setHasUnsavedChanges]);

  const handleDismissCustomerCandidates = useCallback(() => setCustomerCandidates([]), []);

  // ── Vehicle match callbacks ──
  const handleVehicleAutoSelected = useCallback((vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    form.setFieldValue('vehicle_id', vehicle.id);
    setHasUnsavedChanges(true);
    formContext.markFieldDirty('vehicle.year');
    formContext.markFieldDirty('vehicle.make');
    formContext.markFieldDirty('vehicle.model');
    formContext.clearAIVehicleFields();
    formContext.onVehicleSaved(vehicle);
    setVehicleCandidates([]);
    for (const key of Object.keys(formContext.fieldConfidences)) {
      if (key.startsWith('vehicle.')) formContext.markFieldReviewed(key);
    }
    message.success(`Auto-selected: ${vehicle.year} ${vehicle.make} ${vehicle.model}`);
  }, [formContext, form, setSelectedVehicle, setHasUnsavedChanges]);

  const handleVehicleCandidatesFound = useCallback((candidates: VehicleMatch[]) => setVehicleCandidates(candidates), []);

  const handleAcceptVehicleCandidate = useCallback((vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    form.setFieldValue('vehicle_id', vehicle.id);
    setHasUnsavedChanges(true);
    formContext.markFieldDirty('vehicle.year');
    formContext.markFieldDirty('vehicle.make');
    formContext.markFieldDirty('vehicle.model');
    formContext.clearAIVehicleFields();
    formContext.onVehicleSaved(vehicle);
    setVehicleCandidates([]);
    for (const key of Object.keys(formContext.fieldConfidences)) {
      if (key.startsWith('vehicle.')) formContext.markFieldReviewed(key);
    }
    message.success(`Selected: ${vehicle.year} ${vehicle.make} ${vehicle.model}`);
  }, [formContext, form, setSelectedVehicle, setHasUnsavedChanges]);

  const handleDismissVehicleCandidates = useCallback(() => setVehicleCandidates([]), []);

  // ── AI field change callbacks ──
  const handleAICustomerFieldChange = useCallback((fieldPath: string, value: unknown) => {
    const currentApproved = formContext.aiState.customerFieldsApproved ?? {};
    formContext.setAICustomerFields({ ...currentApproved, [fieldPath]: String(value ?? '') });
  }, [formContext]);

  const handleAIVehicleFieldChange = useCallback((fieldPath: string, value: unknown) => {
    const currentApproved = formContext.aiState.vehicleFieldsApproved ?? {};
    formContext.setAIVehicleFields({ ...currentApproved, [fieldPath]: String(value ?? '') });
  }, [formContext]);

  // ── AI search callbacks ──
  const handleAICustomerSearch = useCallback(async (terms: {
    name?: string | null; phone?: string | null; email?: string | null;
  }): Promise<string | null> => {
    const searchParts: string[] = [];
    if (terms.name) searchParts.push(terms.name);
    if (!searchParts.length && !terms.phone) return null;
    try {
      const params: Record<string, string> = {};
      if (searchParts.length) params.search = searchParts.join(' ');
      if (terms.phone) params.phone = terms.phone;
      const response = await api.get<Customer[]>('/customers', { params });
      const results = Array.isArray(response.data) ? response.data : [];
      if (results.length === 0) return null;
      const searchLower = (terms.name ?? '').toLowerCase();
      const phoneDigits = (terms.phone ?? '').replace(/\D/g, '');
      const scored = results.map(c => {
        const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
        const cPhone = (c.phone ?? '').replace(/\D/g, '');
        let score = 0;
        if (searchLower && fullName === searchLower) score = 0.97;
        else if (searchLower && fullName.includes(searchLower)) score = 0.75;
        if (phoneDigits && cPhone === phoneDigits) score = Math.max(score, 0.98);
        else if (phoneDigits && cPhone.includes(phoneDigits)) score = Math.max(score, 0.70);
        return { customer: c, score };
      }).sort((a, b) => b.score - a.score);
      const topMatch = scored[0];
      if (topMatch.score >= 0.95) {
        handleCustomerAutoSelected(topMatch.customer);
        return `Customer matched: ${topMatch.customer.first_name} ${topMatch.customer.last_name} -- linked automatically.`;
      }
      setCustomerCandidates(scored.slice(0, 5).map(s => ({
        customer: s.customer, matchConfidence: s.score,
        phoneMatches: !!(phoneDigits && (s.customer.phone ?? '').replace(/\D/g, '').includes(phoneDigits)),
        matchReasons: [
          ...(searchLower && `${s.customer.first_name} ${s.customer.last_name}`.toLowerCase().includes(searchLower) ? ['name match'] : []),
          ...(phoneDigits && (s.customer.phone ?? '').replace(/\D/g, '').includes(phoneDigits) ? ['phone match'] : []),
        ],
      })));
      return null;
    } catch { return null; }
  }, [handleCustomerAutoSelected]);

  const handleAIVehicleSearch = useCallback(async (terms: { year?: string | number | null; make?: string | null; model?: string | null }): Promise<string | null> => {
    if (!selectedCustomer?.id) return null;
    try {
      const response = await api.get<Vehicle[]>(`/vehicles/customer/${selectedCustomer.id}`);
      const vehicles = Array.isArray(response.data) ? response.data : [];
      if (vehicles.length === 0) return null;
      const filtered = vehicles.filter(v => {
        const yearMatch = !terms.year || String(v.year) === String(terms.year);
        const makeMatch = !terms.make || (v.make ?? '').toLowerCase() === String(terms.make).toLowerCase();
        const modelMatch = !terms.model || (v.model ?? '').toLowerCase() === String(terms.model).toLowerCase();
        return yearMatch && makeMatch && modelMatch;
      });
      if (filtered.length === 1) {
        handleVehicleAutoSelected(filtered[0]);
        return `Vehicle matched: ${filtered[0].year} ${filtered[0].make} ${filtered[0].model} -- linked automatically.`;
      } else if (filtered.length > 1) {
        setVehicleCandidates(filtered.map(v => ({ vehicle: v, matchConfidence: 0.8, matchReasons: ['customer vehicle'] })));
      }
      return null;
    } catch { return null; }
  }, [selectedCustomer, handleVehicleAutoSelected]);

  // ── AI service item callbacks ──
  const handleAIServiceItemChange = useCallback((index: number, field: string, value: unknown) => {
    const oldItem = lineItems[index];
    const oldFieldValue = oldItem ? (oldItem as any)[field] : undefined;
    setLineItems(prev => {
      const next = [...prev];
      if (index >= 0 && index < next.length) {
        const item = { ...next[index], [field]: value } as EstimateLineItem;
        if (field === 'labor_discount_percentage' && Number(value) !== 0) item.labor_discount_amount = 0;
        else if (field === 'labor_discount_amount' && Number(value) !== 0) item.labor_discount_percentage = 0;
        const LABOR_FIELDS = new Set(['labor_hours', 'hourly_rate', 'labor_discount_percentage', 'labor_discount_amount']);
        if (LABOR_FIELDS.has(field)) {
          const hours = Number(item.labor_hours ?? 0);
          const rate = Number(item.hourly_rate ?? 0);
          const baseLaborCost = hours * rate;
          const discountAmt = item.labor_discount_amount
            ? Number(item.labor_discount_amount)
            : (baseLaborCost * Number(item.labor_discount_percentage ?? 0)) / 100;
          const partsCost = (item.parts ?? []).reduce((s, p) => s + ((p as any).discounted_price ?? p.total_price), 0);
          item.total_price = baseLaborCost - discountAmt + partsCost;
        }
        next[index] = item;
      }
      return next;
    });
    logEvent({
      event_id: newEventId(), event_type: 'estimate.service_item.ai_overridden',
      entity_type: 'estimate', entity_id: estimate?.id,
      field_path: `service_items.${index}.${field}`,
      old_value: oldFieldValue != null ? JSON.stringify(oldFieldValue) : undefined,
      new_value: value != null ? JSON.stringify(value) : undefined,
      source: 'manual', ai_was_overridden: true,
    });
    setHasUnsavedChanges(true);
  }, [lineItems, estimate?.id, setLineItems, setHasUnsavedChanges]);

  const handleAIAddServiceItem = useCallback((itemData: Partial<EstimateLineItem>) => {
    setLineItems(prev => {
      const laborHours = Number(itemData.labor_hours) || 0;
      const hourlyRate = Number(itemData.hourly_rate) || defaultLaborRate;
      const partsCost = (itemData.parts ?? []).reduce((s, p) => s + p.total_price, 0);
      const newItem: EstimateLineItem = {
        id: Date.now(), estimate_id: estimate?.id || 0,
        category: itemData.category || 'other', description: itemData.description || 'Service',
        quantity: 1, labor_hours: laborHours, hourly_rate: hourlyRate,
        total_price: laborHours * hourlyRate + partsCost,
        parts: itemData.parts ?? [], approval_status: 'pending',
        labor_confidence: itemData.labor_confidence || 'estimated', ...itemData,
      };
      return [...prev, newItem];
    });
    setHasUnsavedChanges(true);
  }, [defaultLaborRate, estimate?.id, setLineItems, setHasUnsavedChanges]);

  const handleAIRemoveServiceItem = useCallback((index: number) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  }, [setLineItems, setHasUnsavedChanges]);

  // ── AI extraction handler ──
  const handleAIExtraction = useCallback(async (data: any) => {
    setAiExtractedData(data);
    if (data.vehicle?.extracted) {
      const vFields = transformVehicleToFields(data.vehicle.extracted);
      if (vFields.length > 0) {
        const vehicleRecord: Record<string, string> = {};
        for (const f of vFields) vehicleRecord[f.name] = f.value;
        formContext.setAIVehicleFields(vehicleRecord);
      }
    }
    if (data.metadata?.customer_notes) {
      form.setFieldsValue({ customer_notes: data.metadata.customer_notes });
    }
  }, [formContext, form]);

  // ── Persisted meta fields ──
  const lastAppliedMetaRef = useRef<string>('');
  useEffect(() => {
    const { customer_notes, valid_until } = formContext.pendingMetaFields;
    const metaKey = `${customer_notes || ''}|${valid_until || ''}`;
    if (!metaKey || metaKey === '|') return;
    if (lastAppliedMetaRef.current === metaKey) return;
    lastAppliedMetaRef.current = metaKey;
    if (customer_notes) form.setFieldValue('customer_notes', customer_notes);
    if (valid_until) { const parsed = dayjs(valid_until); if (parsed.isValid()) form.setFieldValue('valid_until', parsed); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formContext.pendingMetaFields]);

  // ── AI pending service items auto-insert ──
  const lastAppliedServiceHashRef = useRef<string>('');
  useEffect(() => {
    const items = aiAnalysis.pendingServiceItems;
    if (!items || items.length === 0) return;
    const hash = items.map((i: any) => i.description ?? '').join('|');
    if (lastAppliedServiceHashRef.current === hash) return;
    lastAppliedServiceHashRef.current = hash;
    const newLineItems: EstimateLineItem[] = items.map((item: any, index: number) => {
      const laborHours = Number(item.labor_hours) || 0;
      const hourlyRate = Number(item.suggested_rate) || Number(item.hourly_rate) || defaultLaborRate;
      const aiParts: Part[] = (item.parts || []).map((p: any, pIdx: number) => {
        const qty = Number(p.qty) || 1;
        const unitPrice = p.unit_price != null ? Number(p.unit_price) : null;
        return {
          id: Date.now() + index * 1000 + pIdx, part_number: p.part_number || '',
          description: p.description || 'Part', quantity: qty,
          unit_price: unitPrice ?? 0, total_price: unitPrice != null ? qty * unitPrice : 0,
          availability: 'order_required' as const, is_ai_suggested: true,
          is_optional: p.is_optional === true,
          ai_confidence: typeof p.confidence === 'number' ? p.confidence : undefined,
        };
      });
      const partsCost = aiParts.reduce((sum, p) => sum + p.total_price, 0);
      return {
        id: Date.now() + index, estimate_id: estimate?.id || 0,
        category: item.category || 'other', description: item.description || 'Service',
        quantity: 1, labor_hours: laborHours, hourly_rate: hourlyRate,
        total_price: laborHours * hourlyRate + partsCost,
        parts: aiParts, approval_status: 'pending' as const,
        labor_confidence: (item.labor_confidence as 'verified' | 'estimated' | 'low' | 'researched') || 'estimated',
        labor_research_notes: item.labor_research_notes ?? undefined,
        labor_research_source: item.labor_research_source ?? undefined,
        procedure_steps: item.procedure_steps ?? undefined,
        procedure_id: item.procedure_id ?? undefined,
        parts_researched: item.parts_researched ?? undefined,
        special_tools_researched: item.special_tools_researched ?? undefined,
        procedure_sources: item.procedure_sources ?? undefined,
        parts_sources: item.parts_sources ?? undefined,
        synthesis_confidence: item.synthesis_confidence ?? undefined,
        procedure_author_role: item.procedure_author_role ?? undefined,
        procedure_author_user_id: item.procedure_author_user_id ?? undefined,
        procedure_reviewed_by: item.procedure_reviewed_by ?? undefined,
        procedure_reviewed_at: item.procedure_reviewed_at ?? undefined,
        procedure_match_level: item.procedure_match_level ?? undefined,
        procedure_year_range_warning: item.procedure_year_range_warning ?? undefined,
      };
    });
    if (newLineItems.length > 0) {
      setLineItems(prev => {
        const manualItems = prev.filter(i => i.approval_status !== 'pending');
        return [...manualItems, ...newLineItems];
      });
      setHasUnsavedChanges(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiAnalysis.pendingServiceItems]);

  return {
    aiExtractedData,
    customerCandidates,
    vehicleCandidates,
    handleCustomerAutoSelected,
    handleCustomerCandidatesFound,
    handleAcceptCustomerCandidate,
    handleDismissCustomerCandidates,
    handleVehicleAutoSelected,
    handleVehicleCandidatesFound,
    handleAcceptVehicleCandidate,
    handleDismissVehicleCandidates,
    handleAICustomerFieldChange,
    handleAIVehicleFieldChange,
    handleAICustomerSearch,
    handleAIVehicleSearch,
    handleAIServiceItemChange,
    handleAIAddServiceItem,
    handleAIRemoveServiceItem,
    handleAIExtraction,
  };
}

function transformVehicleToFields(vehicleData: any) {
  const fields: { name: string; value: string }[] = [];
  if (vehicleData.year) fields.push({ name: 'year', value: String(vehicleData.year) });
  if (vehicleData.make) fields.push({ name: 'make', value: vehicleData.make });
  if (vehicleData.model) fields.push({ name: 'model', value: vehicleData.model });
  if (vehicleData.vin) fields.push({ name: 'vin', value: vehicleData.vin });
  if (vehicleData.current_mileage) fields.push({ name: 'mileage', value: String(vehicleData.current_mileage) });
  if (vehicleData.license_plate) fields.push({ name: 'license_plate', value: vehicleData.license_plate });
  if (vehicleData.color) fields.push({ name: 'color', value: vehicleData.color });
  return fields;
}
